/**
 * Encryption Utilities
 * Handles encryption/decryption of IDs for secure URL parameters
 */

import CryptoJS from 'crypto-js'

// Require an explicit key — never ship a hardcoded fallback secret.
const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY

function requireEncryptionKey() {
  if (!ENCRYPTION_KEY) {
    throw new Error('VITE_ENCRYPTION_KEY is required')
  }
  return ENCRYPTION_KEY
}

/**
 * Encrypt an ID for use in URLs
 * @param {string|number} id - The ID to encrypt
 * @returns {string} Encrypted string (base64 encoded)
 */
export function encryptId(id) {
  try {
    const encrypted = CryptoJS.AES.encrypt(String(id), requireEncryptionKey()).toString()
    // URL-safe base64 encoding
    return btoa(encrypted).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  } catch (error) {
    console.error('Encryption error:', error)
    throw new Error('Failed to encrypt ID')
  }
}

/**
 * Decrypt an encrypted ID from URL
 * @param {string} encryptedId - The encrypted ID string
 * @returns {string} Decrypted ID
 */
export function decryptId(encryptedId) {
  try {
    // URL-safe base64 decoding
    const base64 = encryptedId.replace(/-/g, '+').replace(/_/g, '/')
    const encrypted = atob(base64)
    const decrypted = CryptoJS.AES.decrypt(encrypted, requireEncryptionKey())
    const id = decrypted.toString(CryptoJS.enc.Utf8)

    if (!id) {
      throw new Error('Invalid encrypted ID')
    }

    return id
  } catch (error) {
    console.error('Decryption error:', error)
    throw new Error('Failed to decrypt ID. Invalid or corrupted ID.')
  }
}

/**
 * Validate if a string is a valid encrypted ID
 * @param {string} encryptedId - The encrypted ID to validate
 * @returns {boolean} True if valid, false otherwise
 */
export function isValidEncryptedId(encryptedId) {
  try {
    decryptId(encryptedId)
    return true
  } catch {
    return false
  }
}
